import { Button } from "@dropbox/dig-components/buttons";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Shown_DashExtensionUpsell } from "@mirage/analytics/events/types/shown_dash_extension_upsell";
import { createUxaElementId } from "@mirage/analytics/uxa";
import { TeamOnboardingLayout } from "@mirage/growth/components/TeamOnboardingLayout";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { browserNameForCurrentBrowser } from "@mirage/shared/util/webExtensionsHelpers";
import i18n from "@mirage/translations";
import { useCallback, useEffect } from "react";
import { ExtensionUpsell as ExtensionUpsellCtrl } from "../extension-upsell/ExtensionUpsell";
import { ExtensionUpsellButton } from "../extension-upsell/ExtensionUpsellButton";
import styles from "./ExtensionUpsell.module.css";
type Props = {
  onStepFinish: (step: string) => void;
};

export const ExtensionUpsell = ({ onStepFinish }: Props) => {
  const { reportPapEvent } = useMirageAnalyticsContext();

  // When this is ON, we remove the "skip" button to suggest installing the extension
  // is mandatory. In fact, if the user clicks the Install CTA, they are brought to
  // the extension page in a new tab, but they still complete onboarding in their
  // current tab.
  const showSidebarUpsell =
    useFeatureFlagValueBool(
      "otc_growth_2025_04_17_disable_skipping_extension_install_onboarding",
    ) &&
    // Safari does not support the sidebar panel, so keep showing the old version of the upsell.
    browserNameForCurrentBrowser() !== "Safari";

  useEffect(() => {
    reportPapEvent(
      PAP_Shown_DashExtensionUpsell({
        dashActionSurface: "setup",
        featureLine: "extension_upsell",
      }),
    );
  }, [reportPapEvent]);

  const handleFinishOnboarding = useCallback(async () => {
    onStepFinish("complete");
  }, [onStepFinish]);

  const skipButton = (
    <Button
      className={styles.continueButton}
      size="xlarge"
      variant="borderless"
      onClick={handleFinishOnboarding}
    >
      {i18n.t("desktop_onboarding_skip_btn")}
    </Button>
  );

  const extensionUpsellButton = (
    <ExtensionUpsellButton
      surface="team_onboarding"
      onOpenExtensionLinkForCurrentBrowser={handleFinishOnboarding}
      uxaElementId={createUxaElementId(
        "team_onboarding_extension_upsell_link_no_skip",
        {},
      )}
    />
  );

  const title = showSidebarUpsell
    ? i18n.t("team_onboarding_extension_upsell_title_with_sidepanel")
    : i18n.t("team_onboarding_extension_upsell_title");

  const description = showSidebarUpsell
    ? i18n.t("team_onboarding_extension_upsell_description_with_sidepanel")
    : i18n.t("team_onboarding_extension_upsell_description");

  return (
    <TeamOnboardingLayout
      title={title}
      description={description}
      actionButtons={showSidebarUpsell ? extensionUpsellButton : skipButton}
    >
      <ExtensionUpsellCtrl
        showExtensionUpsellButton={!showSidebarUpsell}
        useVideoAsset={!showSidebarUpsell}
        loggingSurface="team_onboarding"
      />
    </TeamOnboardingLayout>
  );
};
